import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useSite } from "../context/site";
import { PageHero, CtaBand, TrajectoryBand } from "../components/site/kit";
import { NextSteps, Pager } from "../components/site/extras";
import { GROWTH_SITUATIONS } from "../lib/content";

export default function WhatWeSolve() {
  const { openDiagnostic } = useSite();
  return (
    <div className="font-body bg-white">
      <PageHero
        testId="whatwesolve-hero"
        eyebrow="What we solve"
        crumbs={[{ label: "Home", href: "/" }, { label: "What We Solve" }]}
        title={<>Four ways growth<br />gets <span className="text-[#5f8f10]">stuck.</span></>}
        subcopy="You do not enter by job title. You enter by the growth problem in front of you. Find the situation that sounds like your business."
      />

      <section className="bg-[#F7F6F2]">
        <div className="mx-auto max-w-[1360px] px-6 py-16 md:px-12 md:py-24 lg:px-16">
          <div className="border-t border-black/15">
            {GROWTH_SITUATIONS.map((s) => (
              <div key={s.id} data-testid={`solve-situation-${s.id}`} className="grid gap-8 border-b border-black/15 py-14 md:grid-cols-12 md:gap-10">
                <div className="md:col-span-5">
                  <Link to={s.href} data-testid={`solve-title-${s.id}`} className="group inline-flex items-start gap-4">
                    <span className="font-display text-4xl leading-none text-[#5f8f10] md:text-5xl">{s.number}</span>
                    <h2 className="font-display text-3xl uppercase leading-[0.95] tracking-tight text-black transition-colors duration-200 group-hover:text-[#5f8f10] md:text-4xl">{s.title}</h2>
                    <ArrowUpRight strokeWidth={2} className="mt-1 h-6 w-6 shrink-0 text-black/40 transition-all duration-200 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#5f8f10]" />
                  </Link>
                  <p className="font-body mt-6 max-w-md text-lg font-normal leading-relaxed text-black/75">{s.blurb}</p>
                </div>
                <div className="md:col-span-7">
                  <p className="font-body text-sm font-semibold uppercase tracking-[0.14em] text-black/50">Signs you may recognise</p>
                  <ul className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
                    {s.symptoms.map((sym, i) => (
                      <li key={i} className="font-body flex items-center gap-3 border-t border-black/12 py-4 text-base font-normal text-black/85">
                        <span className="h-2 w-2 shrink-0 bg-[#5f8f10]" />{sym}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 flex justify-end">
                    <Link to={s.href} data-testid={`solve-explore-${s.id}`} className="group inline-flex items-center gap-2 font-body text-xs font-semibold uppercase tracking-[0.16em] text-black transition-colors hover:text-[#5f8f10]">
                      Explore
                      <ArrowRight strokeWidth={2.5} className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1.5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrajectoryBand src="/assets/editorial-solve.jpg" testId="solve-band" label="Four ways growth gets stuck. One way through." />

      <NextSteps links={[
        { eyebrow: "How we work", title: "The SWITCH model", blurb: "The four moves that turn a stuck situation into growth.", href: "/how-we-work" },
        { eyebrow: "Our work", title: "Proof, not promises", blurb: "See how these situations were resolved for other businesses.", href: "/our-work" },
        { eyebrow: "About", title: "Why we exist", blurb: "A growth company built to turn strategy into execution.", href: "/about" },
      ]} />

      <Pager current="/what-we-solve" />

      <CtaBand
        heading={<>Start with the situation<br />your business is facing.</>}
        onClick={() => openDiagnostic()}
        testId="solve-diagnostic-cta"
      />
    </div>
  );
}
